/**
 * Redis 辞書上書きを i18next にマージするフック
 * 既存の DictionaryContext のキャッシュ・バージョンチェックロジックを移植
 */

import { useState, useEffect, useCallback } from "react";
import i18n from "../../i18n";
import type { DictionaryTerm, DictionaryCache } from "./types";
import { fetchDictionary, checkVersion } from "./api";

const CACHE_KEY = "app_dictionary";
const CACHE_TTL = 60 * 60 * 1000; // 1時間

/**
 * ローカルストレージからキャッシュを取得
 */
function getCache(): DictionaryCache | null {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached) return null;

        const cache: DictionaryCache = JSON.parse(cached);

        if (Date.now() - cache.timestamp > CACHE_TTL) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }

        if (!cache.data || !Array.isArray(cache.data)) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }

        return cache;
    } catch {
        localStorage.removeItem(CACHE_KEY);
        return null;
    }
}

/**
 * ローカルストレージにキャッシュを保存
 */
function setCache(version: string, data: DictionaryTerm[]): void {
    try {
        const cache: DictionaryCache = { version, data, timestamp: Date.now() };
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
        console.warn("Failed to save dictionary cache:", e);
    }
}

/**
 * DictionaryTerm[] を i18next の namespace ごとのリソースに変換
 * キーのドット区切りの先頭を namespace として扱う
 * 例: "menu.home" → namespace "menu", key "home"
 */
function termsToResources(terms: DictionaryTerm[]): Record<string, Record<string, string>> {
    const resources: Record<string, Record<string, string>> = {};

    for (const term of terms) {
        const dotIndex = term.key.indexOf(".");
        if (dotIndex === -1) {
            // namespace なし → common に入れる
            resources["common"] = resources["common"] ?? {};
            resources["common"][term.key] = term.value;
        } else {
            const ns = term.key.substring(0, dotIndex);
            const key = term.key.substring(dotIndex + 1);
            resources[ns] = resources[ns] ?? {};
            resources[ns][key] = term.value;
        }
    }

    return resources;
}

/**
 * Redis 上書き辞書を i18next にマージするフック
 *
 * @param tenantId ログイン中テナントの識別子。API 呼び出しには送らず、
 *                 「テナントが切り替わったら再フェッチする」ためのトリガーとして使う。
 *                 未ログイン時は undefined を渡すとロードをスキップする。
 */
export function useDictionaryOverrides(tenantId?: string): {
    isLoading: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
} {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const applyOverrides = useCallback((terms: DictionaryTerm[]) => {
        const resources = termsToResources(terms);
        for (const [ns, translations] of Object.entries(resources)) {
            i18n.addResourceBundle("ja", ns, translations, true, true);
        }
    }, []);

    const loadOverrides = useCallback(
        async (forceRefresh = false, signal?: AbortSignal) => {
            if (tenantId == null) {
                setIsLoading(false);
                return;
            }

            setError(null);

            try {
                const cache = getCache();

                // キャッシュファースト: キャッシュがあれば即座に適用し、バックグラウンドで検証
                if (cache && !forceRefresh) {
                    applyOverrides(cache.data);
                    setIsLoading(false);

                    // バックグラウンドでバージョン確認＆必要なら更新
                    checkVersion(cache.version, signal)
                        .then((versionCheck) => {
                            if (!versionCheck.is_latest) {
                                return fetchDictionary();
                            }
                            return null;
                        })
                        .then((response) => {
                            if (response) {
                                applyOverrides(response.data);
                                setCache(response.version, response.data);
                            }
                        })
                        .catch(() => {}); // キャッシュ適用済みなのでエラーは無視
                    return;
                }

                // キャッシュなし or 強制リフレッシュ → API から取得
                setIsLoading(true);
                const response = await fetchDictionary();
                applyOverrides(response.data);
                setCache(response.version, response.data);
            } catch (e) {
                console.error("Failed to load dictionary overrides:", e);
                setError(e instanceof Error ? e : new Error("Failed to load dictionary overrides"));

                // エラー時はキャッシュがあれば使用
                const cache = getCache();
                if (cache) {
                    applyOverrides(cache.data);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [tenantId, applyOverrides]
    );

    const refresh = useCallback(async () => {
        await loadOverrides(true);
    }, [loadOverrides]);

    useEffect(() => {
        const controller = new AbortController();
        loadOverrides(false, controller.signal);
        return () => { controller.abort(); };
    }, [loadOverrides]);

    return { isLoading, error, refresh };
}
